<template>
  <div class="relative file-management">
    <div class="ml-3 file-upload-modal">
      <FileDragdrop
        @update:showModal="(state) => (showManagementModal = state)"
        :showModal="showManagementModal"
        uploadIcon="cilPlus"
        upload-icon-size="xl"
        data-cy="upload-input-file"
        :sideModal="true"
        :title="`Add a file from your laptop`"
        :uploadTooltipText="`Upload new file`"
        :isFileUploading="uploadProgress > 0 && uploadProgress < 100"
        :uploadProgressPercentage="uploadProgress"
        :extended="false"
        :showUploadSection="true"
        :i18nImport="''"
        @upload="(files) => uploadSelectedFile(files)"
        multiple
      >
        <template #upload-button>
          <div
            :class="[ctaClasses, ctaPositionalClasses]"
            @click="showManagementModal = true"
          >
            <i class="fa fa-folder"></i>
          </div>
        </template>
        <template #CTA>
          <div
            class="
              text-center
              p-3
              h-100
              mx-3
              d-flex
              align-items-center
              justify-content-center
            "
            style="border: 3px dashed rgb(11, 35, 135); min-height: 200px"
          >
            <div>
              <h4 class="text-muted">Add a new file from local</h4>
              <p class="text-muted">
                Click or drop files here <br />
                {{ allowedExtensions.join(" / ") }}
              </p>
            </div>
          </div>
        </template>
        <template #file-management>
          <div class="mt-2 mb-2 px-4 h5">
            <div v-if="uploadedFiles?.length">
              <div class="d-flex align-items-center justify-content-between">
                Select file to use
                <div class="d-flex align-items-center">
                  <span class="font-weight-bold pr-2" style="font-size: 14px">Filename</span>
                  <RaInput
                    placeholder="Search"
                    style="max-width: 220px"
                    size="sm"
                    class="filter-input pr-3"
                    v-model="filenameFilterValue"
                  />
                </div>
              </div>
            </div>
            <div v-else>No files uploaded</div>
          </div>
          <TableComponent
            v-if="uploadedFiles?.length"
            :key="`uploaded_files_${uploadedFiles.length}`"
            :columns="['filename']"
            :filter-exclude-columns="['row']"
            :data="filesToDisplay"
            theme="cards"
            class="px-4 files-table"
          >
            <template #row="{ row }">
              <CardComponent>
                <template #header>
                  <div class="top-info title text-center mt-2 mb-0">
                    <div
                      class="
                        d-flex
                        align-items-center
                        justify-content-center
                        w-100
                      "
                    >
                      <i
                        class="fa file-icon"
                        :class="`fa-${getIconClass(
                          row?.extension || row?.filename || []
                        )}`"
                      ></i>
                    </div>
                    <span v-c-tooltip="row.filename">
                      {{ row.filename }}
                    </span>
                  </div>
                </template>
                <template #body>
                  <div style="border-top: 1px solid #eee" v-if="row.modified">
                    <div class="d-flex gap-1">
                      <em class="fa fa-calendar mt-1 ml-3" />
                      <div class="w-100">
                        {{ row.modified }}
                      </div>
                    </div>
                    <div
                      class="
                        w-100
                        d-flex
                        align-items-center
                        justify-content-center
                        gap-2
                        mt-2
                      "
                    >
                      <CButton
                        class="ra-icon-button"
                        @click="() => handleDelete(row.filename)"
                        v-c-tooltip="'Delete this file'"
                      >
                        <Icon name="trash" />
                      </CButton>
                      <CButton
                        class="ra-icon-button"
                        @click="
                          () =>
                            $downloadFile({
                              name: row.filename,
                              fileId,
                              query: {
                                ...fileQuery,
                                name: row.filename,
                              },
                            })
                        "
                        v-c-tooltip="'Download this file'"
                      >
                        <i class="fas fa-download"></i>
                      </CButton>
                      <CButton
                        class="ra-icon-button"
                        @click="() => $emit('use', row.filename)"
                        v-c-tooltip="'Use this file'"
                      >
                        <i class="mz-icon icon-attache"></i>
                      </CButton>
                    </div>
                  </div>
                  <div
                    v-else
                    class="d-flex align-items-center justify-content-center"
                    v-c-tooltip="'This file is currently uploading.'"
                  >
                    <CProgress :value="row.progress" />
                  </div>
                </template>
              </CardComponent>
            </template>
          </TableComponent>
        </template>
      </FileDragdrop>
    </div>
  </div>
</template>

<script>
import { FilesService } from "@/services/files";

export default {
  name: "FileManagement",
  props: {
    fileId: Number,
    uploadQuery: {
      type: Object,
      default() {
        return {};
      },
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    ctaClasses: {
      type: String,
      default: "file-management__cta",
    },
    ctaPositionalClasses: {
      type: String,
      default: "file-management__cta--position",
    },
    title: {
      type: String,
      default: "File management",
    },
    customAllowedExtensions: {
      type: Array,
      default: [],
    },
    customUploadedFiles: {
      type: Array,
      default: [],
    }
  },
  data() {
    return {
      uploadedFiles: [],
      allowedExtensions: [],
      filesInUploadQueue: {},
      uploadProgress: 0,
      filenameFilterValue: "",
      showManagementModal: false,
    };
  },
  watch: {
    async showManagementModal() {
      if (this.showManagementModal) {
        await this.getUploadedFileNames();
      }
      else {
        this.$emit("closeModal")
      }
    },
    customUploadedFiles(customUploadedFiles) {
      this.$store.commit("set", ["uploadedFiles", customUploadedFiles || []]);
      this.getUploadedFileNames();
    },
  },
  async mounted() {
    this.$store.commit("set", ["uploadedFiles", this.customUploadedFiles || []]);
    await this.getAllowedExtensions();
    await this.getUploadedFileNames();
  },
  computed: {
    filesToDisplay(){
      const filesList = [...this.uploadQueue, ...this.uploadedFiles];

      if (this.filenameFilterValue) return filesList.filter(file => file.filename.toLowerCase().includes(this.filenameFilterValue))
      return filesList;

    },
    uploadQueue() {
      return Object.keys(this.filesInUploadQueue)
        .filter(
          (name) =>
            !this.uploadedFileNames.includes(name) &&
            this.filesInUploadQueue[name] !== 100
        )
        .map((name) => {
          return { filename: name, progress: this.filesInUploadQueue[name] };
        });
    },
    uploadedFileNames() {
      return this.uploadedFiles.map((file) => file.filename);
    },
    fileQuery() {
      return { file_id: this.fileId, ...this.uploadQuery };
    },
  },
  methods: {
    updateShowManagementModal() {
      this.showManagementModal = !this.showManagementModal
    },
    async getAllowedExtensions() {
      this.allowedExtensions = this.customAllowedExtensions?.length ? this.customAllowedExtensions : (
        await FilesService.getAllowedExtensions(this.fileId)
      ).map((ext) => `.${ext}`);
    },
    getIconClass(ext) {
      if (ext.includes("pdf")) return "file-pdf";
      else if (ext.includes("doc")) return "file-word";
      else if (["xlsx", "csv"].includes(ext)) return "file-spreadsheet";
      else if (["ppt", "ods"].includes(ext)) return "file-powerpoint";
      else if (ext.includes("doc") || ext === "txt") return "file-word";
      return "file";
    },
    async getUploadedFileNames() {
      this.uploadedFiles = this.customUploadedFiles?.length ? this.customUploadedFiles : await FilesService.fetchFiles({
        ...Object.fromEntries(
          Object.entries(this.uploadQuery).filter(([_, v]) => v)
        ),
        ...(this.$store.state.uploadedFiles?.length
          ? { filenames: this.$store.state.uploadedFiles }
          : {}),
      });
    },
    onProgress(value, name = null) {
      if (!name) return (this.uploadProgress = Math.round(value));
      this.filesInUploadQueue[name] = value;
      this.uploadProgress = Math.round(
        (Object.values(this.filesInUploadQueue).filter(
          (progressValue) => progressValue === 100
        ).length /
          Object.keys(this.filesInUploadQueue).length) *
          100
      );
    },
    async uploadSelectedFile(files) {
      const existingFiles = [];
      const filesToUpload = [];
      for (const file of files) {
        const { name } = file;
        const secureFilename = await FilesService.secureFilename(name);
        if (this.uploadedFileNames.includes(secureFilename)) {
          existingFiles.push(secureFilename);
        } else {
          filesToUpload.push({ file: file, name: secureFilename });
        }
      }
      if (existingFiles.length) {
        alert(
          `Files: ${existingFiles.join(
            ", "
          )} already exists. Please rename them or delete and reupload.`,
          {
            color: "warning",
            store: this.$store,
          }
        );
      }
      if (filesToUpload.length) {
        for (const file of filesToUpload) {
          this.handleFileUpload(file.file, file.name);
        }
      }
    },
    async handleDelete(filename) {
      if (Object.keys(this.filesInUploadQueue)?.includes(filename))
        delete this.filesInUploadQueue[filename];
      await this.$deleteResource({
        resourceName: filename,
        callbackFunction: this.getUploadedFileNames,
        query: {
          ...this.fileQuery,
          name: filename,
          ...this.uploadQuery,
        },
      });
      this.$emit("delete");
    },
    async handleFileUpload(file, name) {
      this.filesInUploadQueue[name] = 0;
      if (this.$store.state.uploadedFiles.indexOf(name) === -1) {
        this.$store.commit("set", [
          "uploadedFiles",
          [...this.$store.state.uploadedFiles, name],
        ]);
      }
      this.$uploadFile({
        file: file,
        fileName: name,
        fileId: this.fileId,
        query: this.uploadQuery,
        onLoad: () => this.handleFileUploaded(),
        onProgress: (value) => this.onProgress(value, name),
      });
    },
    async handleFileUploaded() {
      await this.getUploadedFileNames();
      this.$emit("handleFileUploaded")
    },
  },
};
</script>
<style lang="scss">
.file-management {
  .side-modal-wrapper {
    width: 660px !important;
  }
  .progress {
    width: 100% !important;
  }

  thead {
    display: none !important;
  }
}
.files-table {
  .mz-table {
    thead {
      tr {
        th {
          border: none !important;
        }
      }
      tr.VueTables__filters-row {
        th {
          border: none !important;
        }
      }
    }

    tbody {
      grid-gap: 2rem !important;

      tr {
        height: 180px !important;
      }
    }
  }
}

.file-icon {
  font-size: 4em;
}

.file-upload-modal {
  .mz-icon-btn.file-action__btn {
    display: flex;
    justify-content: center;
    align-items: center;
    width: 25px;
    height: 25px;
    font-size: 1.2em;
  }

  .my-card {
    width: 165px !important;
  }
}

.file-management__cta--position {
  position: absolute;
  top: 5px;
  left: 495px;
  z-index: 1;
}

.file-management__cta {
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none !important;

  &:hover {
    background-color: #d6d6d6;
  }
}
</style>
